import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Gear, Sector, Stage, TOOL_NAMES } from '../domain/olmps';
import { DISPLAY_FONT, makeThemedStyles, radii } from '../v10/theme';
import { Hexagon } from './primitives';
import { ToolHost } from './ToolHost';
import { Atmosphere } from './glass';
import { getDeityAvatar } from './deityAvatars';
import { SEAT_BY_ID } from '../v10/roster';
import { Medallion } from '../v10/ui';
import { openTalk } from '../v10/TalkingOverlay';
import { useDataVersion } from '../live/screenContext';

export function ToolPanel({
  sector,
  stage,
  gear,
  toolIds,
  activeToolIndex,
  onSelectToolIndex,
  onClose,
  onOpenInfo,
  toolProps,
  onOpenTool,
}: {
  sector: Sector;
  stage: Stage;
  gear: Gear;
  toolIds: string[];
  activeToolIndex: number;
  onSelectToolIndex: (index: number) => void;
  onClose: () => void;
  onOpenInfo: () => void;
  /** Narrow, additive per-tool preselection (e.g. THEMIS's initialProtocolId
   *  — see StepScreen's Execute cell — or KYRS's initialTaskId, from NUMA's
   *  "Open in KYRS" action). Absent for every other caller. */
  toolProps?: { initialProtocolId?: string; initialTaskId?: string };
  /** Cross-tool navigation (e.g. ORACLE's rings) — see ToolProps.onOpenTool. */
  onOpenTool?: (toolId: string, opts?: { initialTaskId?: string }) => void;
}) {
  const styles = useStyles();
  const toolId = toolIds[activeToolIndex] ?? toolIds[0];

  // Every tool with a deity gets a portrait button (where the old AI on/off
  // toggle sat). Tapping it brings the deity up in the shared TalkingOverlay —
  // phone / chat / video — the same way SyQi's butterfly does. SyqiTool is
  // already SyQi's own interface, so it gets no button.
  const deityEntry = toolId && toolId !== 'syqi' ? getDeityAvatar(toolId) : undefined;
  const seat = toolId ? SEAT_BY_ID[toolId === 'astrs' ? 'astra' : toolId === 'jnus' ? 'janus' : toolId] : undefined;
  // An agent wrote records: remount the tool so it reloads them.
  const dataVersion = useDataVersion();

  return (
    <View style={styles.panel}>
      <Atmosphere />
      {/* Duplicate title bar removed (Joel 2026-07-19): OlmpsHeader already
          names the tool; the ⓘ/✕/hexagon strip doubled it on every screen.
          Multi-tool cells keep a slim hexagon switcher only when there is
          actually more than one tool to switch between. */}
      {toolIds.length > 1 && (
        <View style={styles.switcherRow}>
          {toolIds.map((id, i) => (
            <Hexagon
              key={`${id}-${i}`}
              letter={(TOOL_NAMES[id] ?? id).charAt(0)}
              active={i === activeToolIndex}
              onPress={() => onSelectToolIndex(i)}
            />
          ))}
        </View>
      )}
      <View style={styles.body}>
        {deityEntry && seat ? (
          <View style={styles.deityRow}>
            <Text style={styles.deityLabel}>{deityEntry.deityName}</Text>
            <Pressable
              onPress={() => openTalk(seat.id, seat.name)}
              style={styles.deityBtn}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={`Talk to ${seat.name}`}
            >
              <Medallion id={seat.id} size={34} />
            </Pressable>
          </View>
        ) : null}
        {toolId ? (
          <ToolHost
            key={`${toolId}:${dataVersion}`}
            toolId={toolId}
            sector={sector}
            stage={stage}
            gear={gear}
            initialProtocolId={toolProps?.initialProtocolId}
            initialTaskId={toolProps?.initialTaskId}
            onOpenTool={onOpenTool}
          />
        ) : (
          <View style={styles.empty}>
            <Text style={styles.emptyText}>No tool assigned to this cell yet</Text>
          </View>
        )}
      </View>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    panel: {
      flex: 1,
      borderRadius: radii.panel,
      overflow: 'hidden',
      backgroundColor: 'transparent',
    },
    switcherRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 8,
      paddingHorizontal: 10,
      paddingVertical: 6,
    },
    deityRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingTop: 8, paddingBottom: 4 },
    deityLabel: { fontFamily: DISPLAY_FONT, fontSize: 12, letterSpacing: 2.5, color: t.ivorySoft, textTransform: 'uppercase' },
    deityBtn: { borderRadius: 17 },
    body: { flex: 1 },
    empty: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
    emptyText: { color: t.ivoryFaint, fontStyle: 'italic' },
  }),
);
